/**
 * Application Routes Configuration (Cleaned)
 *
 * Only includes routes for required modules:
 * Assets, Collections, Finance, Leases & Agreements, Work Management
 * Plus core infrastructure routes.
 */

import { MODULE_HEADER_BG } from '@/theme/moduleHeader';
import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import PrivateRoute from '../../components/PrivateRoute';

// Core Pages
const ProfilePage = lazy(() => import('../core/auth/pages/Profile'));
const HelpPage = lazy(() => import('../../pages/help'));
const Themes = lazy(() => import('../../pages/Themes'));
const SupportTicket = lazy(() => import('../../components/SupportTicket'));

// Core Module Pages
const SetupPage = lazy(() => import('../core/setup'));
const CourtTypePage = lazy(() => import('../core/setup/court-type'));
const ReportsPage = lazy(() => import('../core/reports/pages/ReportsList'));
const ReportDetailPage = lazy(() => import('../core/reports/pages/ReportDetail'));
const DataInsightsPage = lazy(() => import('../core/data-insights'));
const DocumentLibraryPage = lazy(() => import('../utility/document-library'));

// Help Desk (ds-helpdesk module) pages — wired as children of /app/ds-helpdesk
// The UDA scope strip: a pathless layout route below, so one instance sits above every
// helpdesk screen — dashboard, tickets, setup masters and reports.
import { HelpdeskUdaScopeBar, HelpdeskUdaSelect } from '../utility/ds-helpdesk/components/HelpdeskUdaScope';
import { ModuleBannerProvider, type ModuleBanner } from '../core/layout/moduleBanner';

// IT-Helpdesk Setup and Reports: the slim module masthead in the helpdesk's slate ->
// indigo -> violet. Setup carries the helpdesk UDA picker; the report screens draw the
// picker the report data obeys themselves. (The strip is lifted on these paths.)
const HELPDESK_BANNER_BG = MODULE_HEADER_BG;
const HELPDESK_SETUP_BANNER: ModuleBanner = { background: HELPDESK_BANNER_BG, title: 'Setup', udaSlot: <HelpdeskUdaSelect tone="dark" compact /> };
const HELPDESK_REPORTS_BANNER: ModuleBanner = { background: HELPDESK_BANNER_BG, title: 'Reports' };
const HdHelpDeskDashboard = lazy(() => import('../utility/ds-helpdesk/pages/HelpDeskDashboard'));
const HdSupportTickets = lazy(() => import('../utility/ds-helpdesk/transactions/support-tickets'));
const HdAgeingAnalysis = lazy(() => import('../utility/ds-helpdesk/pages/AgeingAnalysis'));
const HdTickets = lazy(() => import('../utility/ds-helpdesk/transactions/tickets'));
const HdTicketDetail = lazy(() => import('../utility/ds-helpdesk/transactions/tickets/TicketDetail'));
const HdApplications = lazy(() => import('../utility/ds-helpdesk/masters/application'));
const HdCategories = lazy(() => import('../utility/ds-helpdesk/masters/category'));
const HdTicketTypes = lazy(() => import('../utility/ds-helpdesk/masters/ticket-type'));
const HdPriorities = lazy(() => import('../utility/ds-helpdesk/masters/priority'));
const HdStatuses = lazy(() => import('../utility/ds-helpdesk/masters/status'));
const HdCloseReasons = lazy(() => import('../utility/ds-helpdesk/masters/close-reason'));
const HdSupportTeams = lazy(() => import('../utility/ds-helpdesk/masters/support-team'));
const HdFaqCategories = lazy(() => import('../utility/ds-helpdesk/masters/faq-category'));
const HdFaqs = lazy(() => import('../utility/ds-helpdesk/masters/faq'));
const HdModules = lazy(() => import('../utility/ds-helpdesk/masters/module'));
const HdSubModules = lazy(() => import('../utility/ds-helpdesk/masters/sub-module'));
const HdSubIssueTypes = lazy(() => import('../utility/ds-helpdesk/masters/sub-issue-type'));

// App Designer
import { appDesignerRoutes } from '../core/app-designer/routes';
const DashboardBuilderPage = lazy(() => import('../core/app-designer/modules/dashboard-builder'));
const FormBuilderPageDesigner = lazy(() => import('../core/app-designer/modules/form-builder'));
const PdfEditorPage = lazy(() => import('../core/app-designer/modules/pdf-editor'));
const TemplateListPage = lazy(() => import('../core/app-designer/modules/template-list'));
const WorkflowBuilderPage = lazy(() => import('../core/app-designer/modules/workflow-designer'));
const ConnectionsPage = lazy(() => import('../core/app-designer/modules/connection-settings'));
const ApiManagementPage = lazy(() => import('../core/app-designer/modules/api-management'));
const ProjectsPageDesigner = lazy(() => import('../core/app-designer/modules/projects'));
const ProjectDetailPage = lazy(() => import('../core/app-designer/modules/projects/ProjectProfile'));
const DataModelerPage = lazy(() => import('../core/app-designer/modules/db-designer'));

// App Admin
const AppAdminHome = lazy(() => import('../core/app-admin/AppAdminHome'));
const AppProfileManagement = lazy(() => import('../core/app-admin/modules/app-profiles'));
const MenuItemsMasterPage = lazy(() => import('../core/app-admin/modules/menu-items'));
const SetupOptionsMasterPage = lazy(() => import('../core/app-admin/modules/setup-options'));
const PortalHome = lazy(() => import('../../pages/apps/portal'));
const PortalSite = lazy(() => import('../../pages/apps/portal/site'));

// Audit Module
const AuditPage = lazy(() => import('../core/app-admin/modules/audit'));

// Chat Analytics Module
const ChatAnalyticsPage = lazy(() => import('../core/app-admin/modules/chat-analytics/ChatAnalytics'));

// VDB Management Module
const VDBManagementPage = lazy(() => import('../core/app-admin/modules/vdb-management'));

// Agents Management Module
const AgentsManagementPage = lazy(() => import('../core/app-admin/modules/agents-management'));

// Map Routes
import { mapChildRoutes, publicMapRoutes } from '../utility/map/routes';

// Note: gisRoutes is NOT imported here on purpose. The GIS module registers itself
// at /app/gis via the module registry (modules/index.ts -> utility/gis/index.tsx).
// Spreading gisRoutes into appMainChildRoutes as well used to mount the same pages a
// second time directly under /app, where its 'dashboard' child swallowed the /app index
// redirect and showed the GeoServer dashboard instead of the user's landing page.

// ============================================================================
// Utility Module Routes
// ============================================================================
import { wmsRoutes } from '../utility/ds-wms/routes';
import { operationsRoutes } from '../utility/operations/routes';

// ============================================================================
// Business Module Routes (Only MAUD ERP)
// ============================================================================
import { maudErpRoutes } from '../business/maud-erp/routes/config';

// ============================================================================
// AI Agents Routes
// ============================================================================
import { aiAgentsChildRoutes } from '../core/ai-agents/routes';
import { knowledgeBaseRoutes } from '../core/knowledge-base/routes';

// ============================================================================
// /app/setup routes
// ============================================================================
export const appSetupRoutes: RouteObject = {
  path: '/app/setup',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: [
    {
      index: true,
      element: <SetupPage />
    },
    {
      path: 'court-type',
      element: <CourtTypePage />
    }
  ]
};

// ============================================================================
// /app/reports routes
// ============================================================================
export const appReportsRoutes: RouteObject = {
  path: '/app/reports',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: [
    {
      index: true,
      element: <ReportsPage />
    },
    {
      path: ':id',
      element: <ReportDetailPage />
    }
  ]
};

// ============================================================================
// /app/usermanagement routes
// User Management module — its Reports submenu opens the DB-driven reports list
// (filtered to the "User Management" app via app_rpt_rel_t / app_url_tx).
// ============================================================================
export const appUserManagementRoutes: RouteObject = {
  path: '/app/usermanagement',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: [
    {
      path: 'reports',
      element: <ReportsPage />
    },
    {
      path: 'reports/:id',
      element: <ReportDetailPage />
    }
  ]
};

// ============================================================================
// /app/app-designer routes
// ============================================================================
export const appDesignerRoute: RouteObject = {
  path: '/app/app-designer',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: appDesignerRoutes
};

// ============================================================================
// /app/admin routes
// ============================================================================
export const appAdminRoutes: RouteObject = {
  path: '/app/admin',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: [
    {
      index: true,
      element: <AppAdminHome />
    },
    {
      path: 'app-profiles',
      element: <AppProfileManagement />
    },
    {
      path: 'menu-items',
      element: <MenuItemsMasterPage />
    },
    {
      path: 'setup-options',
      element: <SetupOptionsMasterPage />
    },
    {
      path: 'portal-management',
      children: [
        {
          index: true,
          element: <PortalHome />
        },
        {
          path: 'site/:siteId?',
          element: <PortalSite />
        }
      ]
    },
    {
      path: 'audit',
      element: <AuditPage />
    },
    {
      path: 'chat-analytics',
      element: <ChatAnalyticsPage />
    },
    {
      path: 'vdb-management',
      element: <VDBManagementPage />
    },
    {
      path: 'agents-management',
      element: <AgentsManagementPage />
    }
  ]
};

// ============================================================================
// /app main routes (protected)
// ============================================================================
/**
 * Landing target for a bare /app hit (post-login, session restore, or any
 * navigate('/app')). Mirrors the resolution AuthContext already performs on login
 * (see context/AuthContext.tsx — server landingPageUrl if it is an /app/ route,
 * else /app/maud-erp), so both entry points send a user to the same place.
 */
const AppIndexRedirect = () => {
  const stored = localStorage.getItem('landing_page_url');
  const target = stored && stored.startsWith('/app/') && stored !== '/app/' ? stored : '/app/maud-erp';
  return <Navigate to={target} replace />;
};

export const appMainChildRoutes: RouteObject[] = [
  {
    index: true,
    element: <AppIndexRedirect />
  },
  // GIS pages live at /app/gis/* (registered by the GIS module) — deliberately not
  // spread in here; doing so put them on generic paths like /app/dashboard, /app/layers.
  // Profile & Help
  {
    path: 'profile',
    element: <ProfilePage />
  },
  {
    path: 'help',
    element: <HelpPage />
  },
  {
    path: 'ds-helpdesk',
    children: [
      {
        // PATHLESS layout route: its only job is to render the UDA scope strip above
        // whichever helpdesk page matched, so the dropdown exists on every one of them
        // without each page hosting a copy. Note the module also has its own
        // routes/index.tsx — it is NOT the one the app mounts; these are.
        element: <HelpdeskUdaScopeBar />,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <HdHelpDeskDashboard /> },
          { path: 'tickets', element: <HdTickets /> },
          { path: 'tickets/:id', element: <HdTicketDetail /> },
          { path: 'support-tickets', element: <HdSupportTickets /> },
          // Ageing Analysis report — opened from the "Ageing" button in the Support Tickets header.
          { path: 'ageing-analysis', element: <HdAgeingAnalysis /> },
          { path: 'reports', element: <ModuleBannerProvider value={HELPDESK_REPORTS_BANNER}><ReportsPage /></ModuleBannerProvider> },
          { path: 'reports/:id', element: <ModuleBannerProvider value={HELPDESK_REPORTS_BANNER}><ReportDetailPage /></ModuleBannerProvider> },
          { path: 'setup', element: <ModuleBannerProvider value={HELPDESK_SETUP_BANNER}><SetupPage /></ModuleBannerProvider> },
          { path: 'masters/application', element: <HdApplications /> },
          { path: 'masters/category', element: <HdCategories /> },
          { path: 'masters/ticket-type', element: <HdTicketTypes /> },
          { path: 'masters/priority', element: <HdPriorities /> },
          { path: 'masters/status', element: <HdStatuses /> },
          { path: 'masters/close-reason', element: <HdCloseReasons /> },
          { path: 'masters/support-team', element: <HdSupportTeams /> },
          { path: 'masters/faq-category', element: <HdFaqCategories /> },
          { path: 'masters/faq', element: <HdFaqs /> },
          // 'module' maps to hlpd_cmpnt_lst_t — the DB calls these components.
          { path: 'masters/module', element: <HdModules /> },
          { path: 'masters/sub-module', element: <HdSubModules /> },
          { path: 'masters/sub-issue-type', element: <HdSubIssueTypes /> },
        ]
      }
    ]
  },
  // Map Routes
  ...mapChildRoutes,
  // Analytics & Insights
  {
    path: 'data-insights',
    element: <DataInsightsPage />
  },
  {
    path: 'dashboard-builder',
    element: <DashboardBuilderPage />
  },
  // Document Library
  {
    path: 'document-library',
    element: <DocumentLibraryPage />
  },
  // Design System Routes
  {
    path: 'design-system/form-builder',
    element: <FormBuilderPageDesigner />
  },
  {
    path: 'design-system/pdf-editor',
    element: <PdfEditorPage />
  },
  {
    path: 'design-system/template-list',
    element: <TemplateListPage />
  },
  {
    path: 'design-system/db-relationships',
    element: <DataModelerPage />
  },
  {
    path: 'design-system/workflow-builder',
    element: <WorkflowBuilderPage />
  },
  {
    path: 'design-system/connection-settings',
    element: <ConnectionsPage />
  },
  {
    path: 'design-system/api-management',
    element: <ApiManagementPage />
  },
  {
    path: 'design-system/projects',
    element: <ProjectsPageDesigner />
  },
  {
    path: 'design-system/projects/:hndlr_tx-:prjct_id',
    element: <ProjectDetailPage />
  },
  {
    path: 'design-system/themes',
    element: <Themes />
  }
];

export const appMainRoutes: RouteObject = {
  path: '/app',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: appMainChildRoutes
};

// ============================================================================
// /app/ai-agents routes
// ============================================================================
export const appAiAgentsRoutes: RouteObject = {
  path: '/app/ai-agents',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: aiAgentsChildRoutes
};

// ============================================================================
// /app/knowledge-base routes
// ============================================================================
export const appKnowledgeBaseRoutes: RouteObject = {
  path: '/app/knowledge-base',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: knowledgeBaseRoutes
};

// ============================================================================
// /app/operations routes
// ============================================================================
export const appOperationsRoutes: RouteObject = {
  path: '/app/operations',
  element: <PrivateRoute><Layout /></PrivateRoute>,
  children: operationsRoutes[0].children
};

// ============================================================================
// Export all app routes as an array
// ============================================================================
export const appRoutes: RouteObject[] = [
  appSetupRoutes,
  appReportsRoutes,
  appUserManagementRoutes,
  appDesignerRoute,
  appMainRoutes,
  appAdminRoutes,
  appOperationsRoutes,
  appAiAgentsRoutes,
  appKnowledgeBaseRoutes,
  // Business Module Routes (MAUD ERP only - 5 required modules)
  maudErpRoutes,
  // Utility Module Routes
  publicMapRoutes,
  wmsRoutes,
];

export default appRoutes;
